/*
 * PDP color swap.
 *
 * Colours on this shop are separate products (linked through the
 * custom.product_colors metafield), so switching colour used to be a full page
 * load. This intercepts those swatch links and swaps the contents of <main>
 * with the target product's, then jumps to the top of the page — same end state
 * as a reload, without the white flash and without re-downloading the header,
 * footer, CSS and app scripts.
 *
 * Everything degrades to a normal navigation: no JS, a modified click, a failed
 * fetch or a response that does not look like a PDP all fall through to the
 * anchor's href.
 */
(function () {
  'use strict';

  if (window.__pcdColorSwap) return;
  /* The theme editor tracks navigation itself and re-renders sections through
     its own channel; leave it with plain page loads. */
  if (window.Shopify && window.Shopify.designMode) return;
  window.__pcdColorSwap = true;

  var LINK_SELECTOR = '[data-pcd-color-link]';
  var MAIN_SELECTOR = '#main';
  var PDP_SELECTOR = '.pcd-pf-pdp';

  /* Hover/touch prefetches land here so the click itself is usually instant.
     Entries expire so a long session cannot serve a stale price or stock
     state from a page fetched half an hour ago. */
  var CACHE_MAX_AGE = 3 * 60 * 1000;
  var cache = new Map();
  /* Identity of the newest request; older ones resolve into nothing. */
  var current = null;

  function fetchPage(url) {
    var hit = cache.get(url);
    if (hit && Date.now() - hit.time < CACHE_MAX_AGE) return hit.request;

    var request = fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.text();
    });
    /* A failed prefetch must not poison the click that follows. */
    request.catch(function () { cache.delete(url); });
    cache.set(url, { time: Date.now(), request: request });
    return request;
  }

  function runScripts(root) {
    /* innerHTML never executes scripts, so each one is re-created. External
       sources already in the document (Swiper, app embeds) are skipped: they
       are loaded and their globals still exist, re-running them only costs
       time and duplicate listeners. */
    root.querySelectorAll('script').forEach(function (old) {
      var type = (old.type || '').toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'application/javascript' && type !== 'module') return;

      if (old.src) {
        var src = old.src;
        var existing = document.querySelector('script[src="' + src.replace(/"/g, '\\"') + '"]');
        if (existing && existing !== old) return;
      }

      var fresh = document.createElement('script');
      for (var i = 0; i < old.attributes.length; i++) {
        fresh.setAttribute(old.attributes[i].name, old.attributes[i].value);
      }
      if (!old.src) fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    });
  }

  function copyHead(doc) {
    if (doc.title) document.title = doc.title;

    if (!doc.head) return;

    [
      ['link[rel="canonical"]', 'href'],
      ['meta[name="description"]', 'content'],
      ['meta[property="og:title"]', 'content'],
      ['meta[property="og:description"]', 'content'],
      ['meta[property="og:url"]', 'content'],
      ['meta[property="og:image"]', 'content']
    ].forEach(function (pair) {
      var next = doc.head.querySelector(pair[0]);
      if (!next) return;
      var here = document.head.querySelector(pair[0]);
      if (here) {
        here.setAttribute(pair[1], next.getAttribute(pair[1]) || '');
      } else {
        document.head.appendChild(next.cloneNode(true));
      }
    });
  }

  function scrollToTop() {
    /* theme.css sets scroll-behavior: smooth globally; a colour change should
       land at the top the way a reload does, not animate the whole page. */
    var root = document.documentElement;
    var previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    root.style.scrollBehavior = previous;
  }

  function swap(main, nextMain, doc, url) {
    main.innerHTML = nextMain.innerHTML;
    runScripts(main);
    copyHead(doc);
    scrollToTop();
    document.dispatchEvent(new CustomEvent('pcd:pdp-swapped', { detail: { url: url } }));
  }

  function navigate(url, push, link) {
    var main = document.querySelector(MAIN_SELECTOR);
    if (!main) {
      window.location.assign(url);
      return;
    }

    var token = {};
    current = token;
    main.setAttribute('aria-busy', 'true');
    main.classList.add('pcd-pdp-swapping');
    if (link) link.classList.add('pcd-pf-pdp-color--loading');

    fetchPage(url)
      .then(function (html) {
        if (current !== token) return;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextMain = doc.querySelector(MAIN_SELECTOR);
        /* Guard against colour siblings on a template without the standard
           product layout — those get a real navigation instead. */
        if (!nextMain || !nextMain.querySelector(PDP_SELECTOR)) throw new Error('not a pdp');

        if (push) window.history.pushState({ pcdPdp: url }, '', url);
        swap(main, nextMain, doc, url);

        main.removeAttribute('aria-busy');
        main.classList.remove('pcd-pdp-swapping');
        current = null;
      })
      .catch(function () {
        if (current !== token) return;
        window.location.assign(url);
      });
  }

  function isPlainClick(event) {
    return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  function swappableUrl(link) {
    if (!link || !link.href) return null;
    if (link.target && link.target !== '_self') return null;
    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return null;
    }
    if (url.origin !== window.location.origin) return null;
    return url.href;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest(LINK_SELECTOR);
    if (!link || !isPlainClick(event)) return;
    var url = swappableUrl(link);
    if (!url) return;
    event.preventDefault();
    navigate(url, true, link);
  });

  /* Warm the cache before the click lands. pointerover bubbles (pointerenter
     does not), so one delegated listener covers every swatch, including ones
     that arrive with a later swap. */
  ['pointerover', 'touchstart'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var link = event.target.closest && event.target.closest(LINK_SELECTOR);
      if (!link) return;
      var url = swappableUrl(link);
      if (url) fetchPage(url);
    }, { passive: true });
  });

  /* Mark the entry the visitor landed on so back/forward out of a swap is
     recognisable; entries from other documents never reach this handler. */
  window.history.replaceState(
    Object.assign({}, window.history.state, { pcdPdp: window.location.href }),
    '',
    window.location.href
  );

  window.addEventListener('popstate', function (event) {
    if (!event.state || !event.state.pcdPdp) return;
    navigate(window.location.href, false, null);
  });
})();
